import type { HeatStressState } from './types';
import { held } from './held';

/**
 * Heat stress on a reef as NOAA Coral Reef Watch measures it (M46; its
 * methodology, version 3.1, at coralreefwatch.noaa.gov/product/5km/methodology.php,
 * after Liu et al. 2014 and Heron et al. 2015):
 *
 * - **The Maximum Monthly Mean (MMM)**, the warmest month of the year in a
 *   climatology of 1985 to 2012, each calendar month's mean read off its
 *   trend over those years at 1988.2857 (the mean of 1985 to 1990 and 1993,
 *   the years of the program's first climatology, so the baseline does not
 *   warm with the climate: "to avoid inferring an assumption that corals are
 *   able to adapt as quickly as the climate is warming"). Built from the
 *   game's own record, OISST (`scripts/data/oisst.ts`), and kept as its
 *   height above the game's normal by day of the year.
 * - **The HotSpot**, the day's surface temperature above the MMM.
 * - **Degree Heating Weeks (DHW)**: "the summation, over a 12-week (84 days)
 *   running window up to and including that day (i), of 1/7 of each Coral
 *   Bleaching HotSpot (HSj) value of 1 °C or more", °C-weeks.
 * - **The Bleaching Alert Area**, as the program's table defines it
 *   ("updated in December 2023"): no stress where the HotSpot is 0 or less;
 *   a watch where it is under 1 °C; from 1 °C a warning, and alert levels 1
 *   to 5 from 4, 8, 12, 16 and 20 °C-weeks. Its "Potential Coral Bleaching
 *   and Mortality", level by level from the warning: "Risk of Possible
 *   Bleaching", "Risk of Reef-Wide Bleaching", "Risk of Reef-Wide Bleaching
 *   with Mortality of Heat-Sensitive Corals", "Risk of Multi-Species
 *   Mortality", "Risk of Severe, Multi-Species Mortality (> 50% of corals)",
 *   "Risk of Near Complete Mortality (> 80% of corals)". These are the
 *   program's, read from reefs around the world; Maui Nui's own corals'
 *   answer to heat is measured where the coral is (M47).
 */

/** The window, days, and the smallest HotSpot that counts, °C (definitions, above). */
export const DHW_WINDOW_DAYS = 84;
export const COUNTED_HOTSPOT_C = 1;
/** Days in a week (a definition). */
const WEEK_DAYS = 7;

/** No heat stress: every day of the window under the HotSpot that counts. Frozen, it is copied, never put in a world. */
export const NO_HEAT_STRESS: Readonly<HeatStressState> = Object.freeze({ countedC: Object.freeze(new Array<number>(DHW_WINDOW_DAYS).fill(0)) as number[], peakDhw: 0 });

/**
 * Degree Heating Weeks, °C-weeks, of a window's counted HotSpots: their sum
 * over seven; in water that runs `fallC` cooler than the surface on those
 * days (a deeper reef's, M49, `reef.ts`), each HotSpot less the fall, where
 * it still counts. A day under the count at the surface is under it below
 * too, so the window's counted days hold every day that can count there.
 */
function dhwOf(countedC: readonly number[], fallC = 0): number {
  let sum = 0;
  for (const c of countedC) if (c - fallC >= COUNTED_HOTSPOT_C) sum += c - fallC;
  return held(sum / WEEK_DAYS);
}

/**
 * The heat stress after one more day with this HotSpot, °C: the window moved
 * on a day, the day kept if it counts, and the episode's peak carried while
 * the Degree Heating Weeks stay above 0 (an episode ends when they reach it).
 */
export function nextHeatStress(h: HeatStressState, hotSpotC: number): HeatStressState {
  const countedC = h.countedC.slice(1);
  countedC.push(hotSpotC >= COUNTED_HOTSPOT_C ? held(hotSpotC) : 0);
  const dhw = dhwOf(countedC);
  return { countedC, peakDhw: dhw > 0 ? Math.max(h.peakDhw, dhw) : 0 };
}

/** Degree Heating Weeks today, °C-weeks: at the surface, or where the water runs `fallC` cooler on the counted days (above). */
export function degreeHeatingWeeks(h: HeatStressState, fallC = 0): number {
  return dhwOf(h.countedC, fallC);
}

/** The Bleaching Alert Area's levels, in order (above). */
export const ALERT_LEVELS = ['No stress', 'Bleaching watch', 'Bleaching warning', 'Alert level 1', 'Alert level 2', 'Alert level 3', 'Alert level 4', 'Alert level 5'] as const;
export type AlertLevel = (typeof ALERT_LEVELS)[number];

/** The alert levels' Degree Heating Weeks, °C-weeks, from level 1 (definitions, above). */
const ALERT_DHW = [4, 8, 12, 16, 20];

/** The day's level, from its HotSpot, °C, and the Degree Heating Weeks it brings, °C-weeks. */
export function alertLevel(hotSpotC: number, dhw: number): AlertLevel {
  if (hotSpotC <= 0) return 'No stress';
  if (hotSpotC < COUNTED_HOTSPOT_C) return 'Bleaching watch';
  let level = 0;
  for (const at of ALERT_DHW) if (dhw >= at) level++;
  return ALERT_LEVELS[2 + level] as AlertLevel;
}

/** What each level means, in the program's words where it gives them (above), as the readout says it. */
export const ALERT_MEANINGS: Readonly<Record<AlertLevel, string>> = {
  'No stress': 'no heat stress',
  'Bleaching watch': 'warmer than the warmest month\'s normal, by under 1 °C',
  'Bleaching warning': 'risk of possible bleaching',
  'Alert level 1': 'risk of reef-wide bleaching',
  'Alert level 2': 'risk of reef-wide bleaching with mortality of heat-sensitive corals',
  'Alert level 3': 'risk of multi-species mortality',
  'Alert level 4': 'risk of severe, multi-species mortality (more than 50% of corals)',
  'Alert level 5': 'risk of near complete mortality (more than 80% of corals)',
};
